import React, {
  createContext,
  useContext,
  useEffect,
  useState,
} from "react";
import { IStory } from "../pages/People/people.types";
import { useNavigate, useParams } from "react-router-dom";
import { getStories } from "../api/stories";

type StoriesDataContextType = {
  stories: IStory[];
  storiesLoading: boolean;
  addStory: (story: IStory) => void;
  editStory: (story: IStory) => void;
  removeStory: (uuid: string) => void;
  isMy: boolean;
};

const StoriesDataContext = createContext<StoriesDataContextType | undefined>(
  undefined
);

export const StoriesDataProvider = ({children, creator, isMy = false}: {
  children: React.ReactNode;
  isMy?: boolean;
  creator?: string;
}) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [stories, setStories] = useState<IStory[]>([]);
  const [storiesLoading, setStoriesLoading] = useState(true);

  const addStory = (story: IStory) => {
    setStories((p) => [...p, story]);
  };

  const editStory = (updated: IStory) => {
    setStories(prev =>
      prev.map(story =>
        story.uuid === updated.uuid ? updated : story
      )
    );
  };

  const removeStory = (uuid: string) => {
    setStories((p) => [...p.filter(s => s.uuid !== uuid)])
  }

  const loadStories = async () => {
    if (!isMy && !creator) return;

    setStoriesLoading(true);

    try {
      const response = await getStories(isMy, {creator});
      setStories(response);
    } catch (e) {
      console.log(e);
    } finally {
      setStoriesLoading(false);
    }
  }

  useEffect(() => {
    loadStories();
  }, [isMy, creator]);

  useEffect(() => {
    if (!id && stories.length > 0) navigate(`${isMy ? "/my-stories" : "/stories"}/${stories[0].uuid}`);
  }, [stories, id]);

  return (
    <StoriesDataContext.Provider value={{
      stories,
      storiesLoading,
      addStory,
      editStory,
      removeStory,
      isMy
    }}>
      {children}
    </StoriesDataContext.Provider>
  );
};

export const useStoriesDataContext = () => {
  const context = useContext(StoriesDataContext);
  if (!context) {
    throw new Error("useStoriesContext must be used within a StoriesProvider");
  }
  return context;
};
